import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  badgeClass,
  alertErrorClass,
  spinnerClass,
  btnPrimaryClass,
  btnSecondaryClass,
} from "./dashboardStyles";
import {
  GetSubscriptionAction,
  UpdatePlanAction,
  GetPublicPricingAction,
  StartPaymentAction,
  getUserInfoFromToken,
} from "../../store/action";
import { UserRole } from "../../constants/roles";
import {
  SubscriptionPlan,
  PLAN_ORDER,
  PlanNameKeys,
  pricingByPlan,
  formatMoney,
  BillingPeriod,
  BillingState,
  billingPeriod,
  ANNUAL_FREE_MONTHS,
  PlanLimitCode,
  isPlanLimitError,
} from "../../constants/subscription";
import PlanPriceTag from "../ui/PlanPriceTag";
import { PAYMENT_PROCESSOR } from "../../constants/legal";
import { notify, notifySaveError } from "../../utils/notify";

/** Badge colour per subscription state, in the order they escalate. */
const STATE_TONES = {
  [BillingState.FREE]: "bg-ink-100 text-ink-600",
  [BillingState.TRIAL]: "bg-brand-50 text-brand-700",
  [BillingState.ACTIVE]: "bg-brand-50 text-brand-700",
  [BillingState.LAPSED]: "bg-amber-50 text-amber-700",
  [BillingState.UNKNOWN]: "bg-ink-100 text-ink-600",
};

/**
 * The "Plan & usage" tab of the billing page: the current plan, live usage
 * against its limits, a plan switcher, and — beside them, from `xl` up — the
 * card that pays for it.
 *
 * Two cards rather than one because they answer different questions: the left
 * one is what the business has, the right one is what it is about to pay. The
 * payment card exists only for a paid plan and for people who manage the
 * company; without it the plan card takes the whole width.
 *
 * The price shown is today's, promotion included, which is exactly what the
 * next payment charges. Nobody keeps a price from when they signed up: a
 * business that chose its plan during a 50% promotion pays full price once the
 * promotion has ended (2026-09-23, the owner's call). So when a discount is
 * showing, the panel also says when it stops.
 */
const SubscriptionPanel = () => {
  const { t, i18n } = useTranslation();

  const role = getUserInfoFromToken()?.role;
  const canManage =
    role === UserRole.COMPANY_ADMIN ||
    role === UserRole.SYSTEM_OWNER ||
    role === UserRole.ADMIN;

  const [usage, setUsage] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null); // the plan currently being applied

  // The payment NETOPIA has opened but the customer has not yet confirmed. Held
  // rather than redirected to straight away: the pricing page is in euro and the
  // card is charged in lei, so this is the one moment to state the real amount
  // and the rate that produced it before anyone leaves for a payment form.
  const [checkout, setCheckout] = useState(null);
  const [starting, setStarting] = useState(false);

  // How much to buy: a month, or a year billed as ten months.
  const [periodMonths, setPeriodMonths] = useState(BillingPeriod.MONTHLY);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsage(await GetSubscriptionAction());
    } catch (err) {
      // 403 = no organization yet (mid-onboarding); not an error worth showing.
      if (err?.response?.status === 403) {
        setUsage(null);
      } else {
        setError(t("Dashboard.Plan.loadError"));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  // What each plan costs today — for the switcher tiles, and for saying how the
  // current price compares. Separate from `load` because a failure here must not
  // stop the usage meters rendering: the tiles fall back to the shipped prices.
  useEffect(() => {
    let cancelled = false;

    GetPublicPricingAction()
      .then((data) => {
        if (!cancelled) setPricing(data);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const quotes = pricingByPlan(pricing);

  const changePlan = async (plan) => {
    if (plan === usage?.plan) return;
    setSaving(plan);
    try {
      setUsage(await UpdatePlanAction(plan));
      notify.success(
        t("Dashboard.Plan.changed", { plan: t(PlanNameKeys[plan]) }),
      );
    } catch (err) {
      // The one refusal worth explaining: a team bigger than the free tier's
      // single specialist cannot be moved onto it until the extras are gone.
      if (isPlanLimitError(err, PlanLimitCode.STAFF)) {
        notify.warning(t("Dashboard.Plan.tooManyStaffForPlan"));
      } else {
        notifySaveError(err, t("Dashboard.Plan.changeError"));
      }
    } finally {
      setSaving(null);
    }
  };

  /**
   * Opens an order and shows what it will actually cost, in lei.
   *
   * Two steps rather than one. The plan screen quotes euro; the card is charged
   * lei, VAT included. Sending someone straight to a payment form would have
   * them meet both conversions at once, on someone else's page — and NETOPIA's
   * own validation checklist asks for the transaction currency to be stated
   * plainly before that point.
   */
  const beginPayment = async () => {
    setStarting(true);
    try {
      setCheckout({
        ...(await StartPaymentAction(periodMonths)),
        periodMonths,
      });
    } catch (err) {
      notifySaveError(err, t("Dashboard.Plan.paymentStartError"));
    } finally {
      setStarting(false);
    }
  };

  // A paid plan has no hard seat cap (PlanLimit.MaxStaff is null on all of
  // them) — adding someone past the included count just bills for them, it
  // never blocks. So "purchased" isn't a number chosen ahead of time, it's what
  // the team's current size is already being charged for: the included seats,
  // or the actual headcount once that exceeds them. Occupied can never pass it.
  // The free tier is capped, and its cap is the seat count instead.
  const seatsPurchased = (usage?.includedSeats ?? 0) + (usage?.extraSeats ?? 0);

  // What the extra seats add to the month. The seat price already carries any
  // live promotion, so this is the same figure the checkout charges for them.
  const extraSeatsCost = (usage?.extraSeats ?? 0) * (usage?.extraSeatPrice ?? 0);

  /** A bar that fills as `used` approaches `limit`; amber only on a hard cap. */
  const meter = (label, value, used, limit, capped) => {
    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    const atCap = capped && used >= limit;
    return (
      <div>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-x-2 text-fluid-sm">
          <span className="text-ink-600">{label}</span>
          <span
            className={atCap ? "font-semibold text-amber-700" : "text-ink-500"}
          >
            {value}
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
          <div
            className={`h-full rounded-full ${atCap ? "bg-amber-500" : "bg-brand-500"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  };

  const staffMeter = (used, seats, capped) =>
    meter(
      t("Dashboard.Plan.teamMembers"),
      t("Dashboard.Plan.seatsUsed", { used, count: seats }),
      used,
      seats,
      capped,
    );

  // Nothing to show yet, or nothing to show at all: one card, no columns.
  if (loading || !usage) {
    return (
      <div className={cardClass}>
        <div className={cardBodyClass}>
          {error && (
            <div className={alertErrorClass} role="alert">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <div className={spinnerClass} role="status" />
            </div>
          ) : (
            <p className="m-0 text-fluid-sm text-ink-500">
              {t("Dashboard.Plan.noOrganization")}
            </p>
          )}
        </div>
      </div>
    );
  }

  const period = billingPeriod(usage);

  // Only for the people who manage the company, and only for a plan that costs
  // something: the free tier has no payment to make, and offering one would be
  // offering to take money for nothing.
  const showPayment = canManage && usage.plan !== SubscriptionPlan.STARTER;

  return (
    <div
      className={`grid items-start gap-6 ${
        showPayment ? "xl:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]" : ""
      }`}
    >
      {/* --- What the business has ----------------------------------------- */}
      <section className={cardClass} data-testid="plan-card">
        {/* The state badge is what the old summary strip above the panels
            said in a box of its own: trial, active, lapsed or free. */}
        <div className="flex items-start justify-between gap-3 border-b border-ink-200 px-5 py-4">
          <div className="min-w-0">
            <p className="m-0 text-xs font-semibold tracking-wide text-ink-500 uppercase">
              {t("Dashboard.Billing.currentPlan")}
            </p>
            <h3 className="m-0 mt-0.5 text-xl font-bold text-ink-900">
              {t(PlanNameKeys[usage.plan])}
            </h3>
          </div>
          <span className={`${badgeClass} shrink-0 ${STATE_TONES[period.state]}`}>
            {t(`Dashboard.Billing.state.${period.state}`)}
          </span>
        </div>

        <div className={`${cardBodyClass} flex flex-col gap-5`}>
          {/* What the next payment costs: today's price, promotion included —
              see the component header. */}
          {usage.price != null && (
            <div className="rounded-lg border border-ink-200 bg-ink-50 p-4">
              <p className="m-0 text-xs font-semibold text-ink-600">
                {t("Dashboard.Plan.yourPrice")}
              </p>
              <p className="m-0 mt-1 flex flex-wrap items-baseline gap-x-2">
                {usage.discountPercent > 0 &&
                  usage.listPrice != null &&
                  usage.listPrice > usage.price && (
                    <span className="text-base text-ink-400 line-through">
                      {formatMoney(usage.listPrice, usage.currency)}
                    </span>
                  )}
                <span className="text-3xl font-bold tabular-nums text-ink-900">
                  {formatMoney(usage.price, usage.currency)}
                </span>
                <span className="text-sm text-ink-500">
                  / {t("Pricing.period")}
                </span>
              </p>

              {/* The included seats are stated even when none is extra,
                  because "3 included" is the number that tells the owner when
                  the next hire starts costing money — which is the whole point
                  of saying it before it does. What the extras cost has its own
                  section below; only their effect on the total is here. */}
              {usage.extraSeatPrice != null && (
                <p className="m-0 mt-1 text-xs text-ink-500">
                  {t("Dashboard.Plan.seatsIncluded", {
                    count: usage.includedSeats,
                  })}
                </p>
              )}

              {usage.extraSeatPrice != null && usage.extraSeats > 0 && (
                <p className="m-0 mt-1 text-xs font-semibold text-ink-800">
                  {t("Dashboard.Plan.estimatedTotal")}{" "}
                  {formatMoney(usage.estimatedMonthlyTotal, usage.currency)}
                </p>
              )}

              {/* A discount lasts only as long as its promotion — nobody keeps
                  it afterwards — so the end date is stated where the decision
                  to pay is made. */}
              {usage.discountPercent > 0 && usage.promotionName && (
                <p
                  className="m-0 mt-3 flex items-start gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-xs text-brand-800"
                  data-testid="plan-promotion"
                >
                  <i className="bi bi-tag mt-px shrink-0" aria-hidden="true" />
                  <span className="min-w-0">
                    {usage.promotionEndsAt
                      ? t("Dashboard.Plan.promotionUntil", {
                          name: usage.promotionName,
                          percent: usage.discountPercent,
                          // The end is exclusive: a sale ending at midnight on
                          // the 1st last applies on the 30th, which is the day
                          // to name.
                          date: new Date(
                            new Date(usage.promotionEndsAt).getTime() - 1,
                          ).toLocaleDateString(i18n.language),
                        })
                      : usage.promotionName}
                  </span>
                </p>
              )}
            </div>
          )}

          {/* The free tier has no clock and no date; say so, rather than leave
              somebody hunting for a deadline that does not exist. */}
          {period.state === BillingState.FREE && canManage && (
            <p className="m-0 text-fluid-sm text-ink-500">
              {t("Dashboard.Billing.freeNote")}
            </p>
          )}

          {/* Usage */}
          <div className="flex flex-col gap-4">
            {usage.staffLimit != null
              ? staffMeter(usage.staffUsed, usage.staffLimit, true)
              : staffMeter(usage.staffUsed, seatsPurchased, false)}

            {/* No ceiling, nothing to measure: a bar against "unlimited"
                only ever looked empty or arbitrary. */}
            {usage.bookingsPerMonthLimit == null ? (
              <div
                className="flex items-center gap-2 text-fluid-sm"
                data-testid="bookings-unlimited"
              >
                <i
                  className="bi bi-check-circle-fill text-brand-600"
                  aria-hidden="true"
                />
                <span className="text-ink-600">
                  {t("Dashboard.Plan.unlimitedBookings")}
                </span>
              </div>
            ) : (
              meter(
                t("Dashboard.Plan.bookingsThisMonth"),
                `${usage.bookingsThisMonth} / ${usage.bookingsPerMonthLimit}`,
                usage.bookingsThisMonth,
                usage.bookingsPerMonthLimit,
                true,
              )
            )}

            <div className="flex items-center gap-2 text-fluid-sm">
              <i
                className={`bi ${
                  usage.hasAnalytics
                    ? "bi-check-circle-fill text-brand-600"
                    : "bi-lock-fill text-ink-400"
                }`}
                aria-hidden="true"
              />
              <span className="text-ink-600">
                {usage.hasAnalytics
                  ? t("Dashboard.Plan.analyticsIncluded")
                  : t("Dashboard.Plan.analyticsLocked")}
              </span>
            </div>
          </div>

          {/* Extra seats: how many the team's size is paying for beyond the
              included ones, and what they add to the month. Only on a plan
              that sells seats — the free tier caps the team instead. Shown at
              zero too, with the seat price, so the cost of the next hire is
              known before it happens. */}
          {usage.extraSeatPrice != null && (
            <div
              className="rounded-lg border border-ink-200 p-3"
              data-testid="extra-seats"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-xs font-semibold text-ink-600">
                  {t("Dashboard.Plan.extraSeatsTitle")}
                </span>
                <span className="flex items-baseline gap-2">
                  <span className="text-lg font-bold tabular-nums text-ink-900">
                    {formatMoney(extraSeatsCost, usage.currency)}
                  </span>
                  <span className="text-xs text-ink-400">
                    / {t("Pricing.period")}
                  </span>
                </span>
              </div>
              <p className="m-0 mt-1 text-xs text-ink-500">
                {usage.extraSeats > 0
                  ? t("Dashboard.Plan.extraSeatsPurchased", {
                      count: usage.extraSeats,
                      price: formatMoney(usage.extraSeatPrice, usage.currency),
                    })
                  : t("Dashboard.Plan.extraSeatsNone", {
                      price: formatMoney(usage.extraSeatPrice, usage.currency),
                    })}
              </p>
            </div>
          )}

          {/* Plan switcher. A row per plan on a phone, a tile per plan from
              `sm` up. */}
          {canManage && (
            <div>
              <p className="mb-2 text-sm font-semibold text-ink-900">
                {t("Dashboard.Plan.changePlan")}
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                {/* A company on a retired plan (Business) still has to see the
                    one it is on, marked current — otherwise the switcher shows
                    three plans, none of them theirs, and reads like their
                    subscription has gone missing. */}
                {(PLAN_ORDER.includes(usage.plan)
                  ? PLAN_ORDER
                  : [...PLAN_ORDER, usage.plan]
                ).map((plan) => {
                  const isCurrent = plan === usage.plan;
                  const quote = quotes[plan];
                  // Custom is quoted, not self-service — never a clickable
                  // choice here, only ever informational.
                  const isQuoteOnly = quote?.price == null;
                  return (
                    <button
                      key={plan}
                      type="button"
                      disabled={isQuoteOnly || isCurrent || saving != null}
                      aria-pressed={isQuoteOnly ? undefined : isCurrent}
                      aria-disabled={isQuoteOnly || undefined}
                      onClick={isQuoteOnly ? undefined : () => changePlan(plan)}
                      className={`flex min-h-11 items-center justify-between gap-3 rounded-lg border p-3 text-left transition disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 sm:flex-col sm:items-start sm:justify-start ${
                        isQuoteOnly
                          ? "border-ink-200 bg-ink-50 opacity-70"
                          : isCurrent
                            ? "border-brand-600 bg-brand-50"
                            : "border-ink-200 bg-white hover:border-brand-300 disabled:opacity-60"
                      }`}
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="font-semibold text-ink-900">
                          {t(PlanNameKeys[plan])}
                        </span>
                        <PlanPriceTag
                          quote={quote}
                          size="sm"
                          className="mt-0.5 text-xs text-ink-500"
                        />
                      </span>
                      <span className="shrink-0 text-xs font-semibold text-brand-700 sm:mt-auto sm:pt-2">
                        {isCurrent
                          ? t("Dashboard.Plan.current")
                          : isQuoteOnly
                            ? t("Dashboard.Plan.contactUs")
                            : saving === plan
                              ? t("Dashboard.Plan.applying")
                              : t("Dashboard.Plan.select")}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* --- Paying for it -------------------------------------------------- */}
      {showPayment && (
        <section className={cardClass} data-testid="payment-card">
          <div className={cardHeaderClass}>
            <h3 className={cardTitleClass}>{t("Dashboard.Plan.payPeriod")}</h3>
          </div>
          <div className={cardBodyClass}>
            {checkout ? (
              /* What it actually costs, before anyone leaves for a card form.
                 The euro figure, the rate, the VAT and the total — the
                 customer meets no surprises on NETOPIA's page. */
              <div
                className="rounded-lg border border-brand-200 bg-brand-50 p-4"
                data-testid="checkout-confirm"
              >
                <p className="m-0 text-sm font-semibold text-ink-900">
                  {t("Dashboard.Plan.youWillPay", {
                    amount: formatMoney(
                      checkout.amountCents / 100,
                      checkout.currency,
                    ),
                  })}
                </p>
                <p className="m-0 mt-1 text-xs font-semibold text-ink-700">
                  {checkout.periodMonths === BillingPeriod.ANNUAL
                    ? `${t("Dashboard.Plan.payAnnual")} · ${t(
                        "Pricing.billing.freeMonths",
                        {
                          count: usage.annualFreeMonths ?? ANNUAL_FREE_MONTHS,
                        },
                      )}`
                    : t("Dashboard.Plan.payMonthly")}
                </p>
                <p className="m-0 mt-1 text-xs text-ink-500">
                  {t("Dashboard.Plan.conversionNote", {
                    source: formatMoney(
                      checkout.sourceAmountCents / 100,
                      checkout.sourceCurrency,
                    ),
                    rate: checkout.exchangeRate,
                    date: new Date(
                      checkout.exchangeRateDate,
                    ).toLocaleDateString(i18n.language),
                    vat: checkout.vatPercent,
                  })}
                </p>

                {/* Side by side while the card is wide, stacked once it sits
                    in the narrow right-hand column. */}
                <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                  {/* A plain link, not a fetch: this leaves our application
                      for NETOPIA's hosted page, which is what keeps card
                      details away from this server entirely. */}
                  <a
                    href={checkout.paymentUrl}
                    className={`${btnPrimaryClass} min-h-11`}
                    data-testid="continue-to-payment"
                  >
                    {t("Dashboard.Plan.continueToPayment")}
                  </a>
                  <button
                    type="button"
                    className={`${btnSecondaryClass} min-h-11`}
                    onClick={() => setCheckout(null)}
                  >
                    {t("Common.cancel")}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* The three things worth saying out loud, in the order they
                    become urgent. */}
                {usage.hasLapsed && (
                  <p
                    className="m-0 mb-3 text-sm text-amber-700"
                    data-testid="plan-lapsed"
                  >
                    <i className="bi bi-exclamation-triangle mr-1" aria-hidden="true" />
                    {t("Dashboard.Plan.lapsed")}
                  </p>
                )}

                {usage.isOnTrial && (
                  <p
                    className="m-0 mb-3 text-sm text-ink-600"
                    data-testid="trial-notice"
                  >
                    <i className="bi bi-hourglass-split mr-1" aria-hidden="true" />
                    {t("Dashboard.Plan.trialDaysLeft", {
                      count: usage.trialDaysLeft ?? 0,
                    })}
                  </p>
                )}

                {!usage.hasLapsed && !usage.isOnTrial && usage.currentPeriodEnd && (
                  <p className="m-0 mb-3 text-sm text-ink-600">
                    <i className="bi bi-check-circle mr-1" aria-hidden="true" />
                    {t("Dashboard.Plan.activeUntil", {
                      date: new Date(usage.currentPeriodEnd).toLocaleDateString(
                        i18n.language,
                      ),
                    })}
                  </p>
                )}

                {/* A month, or a year billed as ten. The figures are the
                    server's estimate at today's team size, net of VAT and in
                    euro — the lei amount comes on the next step. The card's
                    heading says what this is; the legend repeats it for a
                    screen reader. */}
                <fieldset className="m-0 mb-4 border-0 p-0">
                  <legend className="sr-only">{t("Dashboard.Plan.payPeriod")}</legend>
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                    {[BillingPeriod.MONTHLY, BillingPeriod.ANNUAL].map((value) => {
                      const yearly = value === BillingPeriod.ANNUAL;
                      const selected = periodMonths === value;
                      const total = yearly
                        ? usage.estimatedAnnualTotal
                        : usage.estimatedMonthlyTotal;
                      const freeMonths = usage.annualFreeMonths ?? ANNUAL_FREE_MONTHS;
                      return (
                        <label
                          key={value}
                          className={`flex min-h-11 cursor-pointer items-start gap-2.5 rounded-lg border p-3 text-sm transition ${
                            selected
                              ? "border-brand-600 bg-brand-50"
                              : "border-ink-200 bg-white hover:border-brand-300"
                          }`}
                        >
                          <input
                            type="radio"
                            name="pay-period"
                            value={value}
                            checked={selected}
                            onChange={() => setPeriodMonths(value)}
                            className="mt-0.5 size-4 shrink-0 accent-brand-600"
                            data-testid={`pay-period-${yearly ? "annual" : "monthly"}`}
                          />
                          <span className="flex min-w-0 flex-col">
                            <span className="font-semibold text-ink-900">
                              {yearly
                                ? t("Dashboard.Plan.payAnnual")
                                : t("Dashboard.Plan.payMonthly")}
                              {total != null && (
                                <span className="font-normal text-ink-600">
                                  {" — "}
                                  {formatMoney(total, usage.currency)}
                                </span>
                              )}
                            </span>
                            {yearly && freeMonths > 0 && (
                              <span className="text-xs font-semibold text-brand-700">
                                {t("Pricing.billing.freeMonths", {
                                  count: freeMonths,
                                })}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>

                <button
                  type="button"
                  className={`${btnPrimaryClass} min-h-11 w-full`}
                  disabled={starting}
                  onClick={beginPayment}
                  data-testid="pay-button"
                >
                  {starting
                    ? t("Dashboard.Plan.preparingPayment")
                    : usage.currentPeriodEnd && !usage.hasLapsed
                      ? t("Dashboard.Plan.renew")
                      : t("Dashboard.Plan.payNow")}
                </button>

                <p className="m-0 mt-2 text-xs text-ink-500">
                  {t("Dashboard.Plan.chargedInRon")}
                </p>

                {/* The processor, named and shown, at the moment it becomes
                    relevant — one click before the card form. NETOPIA asks for
                    their mark alongside Visa and Mastercard, which is how this
                    asset is drawn. */}
                <a
                  href={PAYMENT_PROCESSOR.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-block"
                >
                  <img
                    src={PAYMENT_PROCESSOR.logo}
                    alt={PAYMENT_PROCESSOR.name}
                    width={910}
                    height={219}
                    className="h-6 w-auto opacity-80 transition hover:opacity-100"
                  />
                </a>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default SubscriptionPanel;
